import { Button, Col, Form, Row } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { RouteChildrenProps, useLocation } from "react-router";
import { ReportActivitiesEnum, ReportStatesEnum, ReportStatusEnum } from "../../../../../../constants/constants";
import { ROUTE_PATHS } from "../../../../../../constants/url-config";
import { FormSectionProps } from "../../../../../../models/form-section.model";
import { ReviewModel } from "../../../../../../models/review.model";
import { ScenarioMitigationModel } from "../../../../../../models/scenario-mitigation.model";
import { setActions, setStatus, setTitle } from "../../../../../../redux/actions/header.action";
import { ECAPService } from "../../../../../../services/ecap.service";
import { ERMService } from "../../../../../../services/erm.service";
import { DATE_UTIL } from "../../../../../../utils/dateUtils";
import i18n from "../../../../../../utils/i18n";
import { ROLE_GUARD } from "../../../../../../utils/role-guard";
import UTIL from "../../../../../../utils/utils";
import AppAnchor from "../../../../../atoms/anchor";
import FooterAction from "../../../../../atoms/footer-action";
import AppReview from "../../../../../atoms/review";
import ControlEffectiveness from "./control-effectiveness-section";
import ResidualRickSection from "./residual-risk-section";
import RiskSeverityImpact from "./risk-severity-impact-section";
import "./scenario-mitigation-view.scss";
import SourceSection from "./source-section";

export interface MitigationSectionViewProps extends FormSectionProps<ScenarioMitigationModel> {
    gogoAppraisalTool?: (state?: ReportStatesEnum) => void;
}

interface Props extends RouteChildrenProps<{ scenarioId: string }> { }
const ScenarioMitigationView = (props: Props) => {

    const { history, match } = props;
    const { t } = useTranslation();
    const scenatioId = React.useRef(0);
    const id = useRef<number | undefined>(undefined);
    const [data, setData] = useState<ScenarioMitigationModel>({});
    const [isApprovalWFL, setIsApprovalWFL] = useState(false);
    const [reviewData, setReviewData] = useState<ReviewModel[]>();
    const [newReview, setNewReview] = useState<ReviewModel>();
    const location = useLocation();
    const dispatch = useDispatch();
    const isOwner = useRef<boolean>(false);

    const sections = useRef([
        {
            title: "",
            id: 2,
            is_completed: false,
            sectionComponent: SourceSection
        },
        {
            title: t("ScenarioMitigation.lbRiskSeverityImpact"),
            id: 3,
            is_completed: false,
            sectionComponent: RiskSeverityImpact
        },
        {
            title: t("ScenarioMitigation.ttControlEffectiveness"),
            id: 4,
            is_completed: false,
            sectionComponent: ControlEffectiveness
        },
        {
            title: i18n.t("Residual Risk"),
            id: 5,
            is_completed: false,
            sectionComponent: ResidualRickSection
        }
    ])


    const gotoAppraisalTool = (state?: ReportStatesEnum) => {
        if (isOwner.current && UTIL.checkCanEditByState(state)) {
            history.push(`${ROUTE_PATHS.ScenarioAppraisal}/${scenatioId.current}`);
        } else {
            history.push(`${ROUTE_PATHS.ScenarioAppraisalView}/${scenatioId.current}`);
        }
    }


    const initFormData = (val: ScenarioMitigationModel) => {
        id.current = val.id;
        isOwner.current = ROLE_GUARD.isCurrentUser(val.created_by);
        sections.current[0].title = `${t("ScenarioMitigation.lbSourceOf")} ${val.loss_scenario?.name}`;
        UTIL.setSectionIsCompleted(val._section_statuses, sections.current);
        setData(val);
        dispatch(setTitle(`${val.loss_scenario?.name} - ${t("ScenarioMitigation.viewTitle")}`));
        updateStatus(val.submit_state);
    }

    const comment = (message: string) => {
        ERMService.createMitigationCommentAsync(scenatioId.current, message).then(res => {
        })
    }

    const approve = () => {
        ECAPService.approveOrReferMitigationAsync(scenatioId.current, ReportStatusEnum.Approved).then(res => {
            setData(pre => {
                pre.submit_state = ReportStatesEnum.Approved;
                updateStatus(ReportStatesEnum.Approved);
                return pre;
            })

            setNewReview({ action: ReportActivitiesEnum.Approve })
        });
    }

    const refer = () => {
        ECAPService.approveOrReferMitigationAsync(scenatioId.current, ReportStatusEnum.Referred).then(res => {
            setData(pre => {
                pre.submit_state = ReportStatesEnum.Rejected;
                updateStatus(ReportStatesEnum.Rejected);
                return pre;
            })
            setNewReview({ action: ReportActivitiesEnum.Refer })
        });
    }

    const updateStatus = (submit_state: string | undefined) => {
        const dom = UTIL.getLossStatesDOM(submit_state);
        const status = <span className="text-status" style={{ color: dom.color, backgroundColor: dom.bgColor }}>
            {dom.text}
        </span>

        dispatch(setActions(renderActions(submit_state)));

        dispatch(setStatus(status));
    }

    const goBack = () => {
        history.push(ROUTE_PATHS.Scenarios);
    }

    const edit = () => {
        history.push(`${ROUTE_PATHS.ScenarioMitigation}/${scenatioId.current}`);
    }

    const renderActions = (submit_state?: string) => {
        if (submit_state === ReportStatesEnum.Rejected && isOwner.current) {
            return <>
                <Button onClick={goBack} htmlType="button" type="default" shape="round" block >{t("ScenarioMitigation.btnGoback")}</Button>
                <Button onClick={edit} htmlType="button" type="primary" shape="round" block >{t("ScenarioMitigation.btnEdit")}</Button>
            </>;
        }

        return null;
    }



    useEffect(() => {

        scenatioId.current = Number(match?.params.scenarioId);
        setIsApprovalWFL(location.pathname.includes(ROUTE_PATHS.ApprovalWorkflowMitigation));

        ERMService.getMitigationAsyns(scenatioId.current).then(res => {
            initFormData(res.data);
        });

        ERMService.getMitigationCommentsAsync(scenatioId.current).then(res => {
            setReviewData(res.data);
        });

        return () => {
            dispatch(setStatus(""));
            dispatch(setActions(undefined));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <div className='scenario-mitigation-form-wrapper'>
            <Row gutter={20}>
                <Col xs={0} sm={5}>
                    <AppAnchor sections={sections.current} />
                </Col>
                <Col xs={0} sm={14}>
                    <Form
                        className="main-form"
                        layout='vertical'>

                        <div className="form-header">
                            <div className="title">
                                {data.loss_scenario?.name}
                            </div>
                            <div className="sub-title">
                                {t("ControlFailure.lbCreatedOn")} {DATE_UTIL.convertServerToViewDateTimeFormat(data.created_on)}
                            </div>

                        </div>

                        <div className="form-section">
                            <div className="section-wrapper">
                                <Row gutter={[16, 32]}>
                                    <Col span={24}>
                                        <Form.Item
                                            label={t("ScenarioMitigation.lbScenarioOwner")}
                                        >
                                            <span>{data.owner?.name}</span>
                                        </Form.Item>

                                    </Col>
                                </Row>
                                <Row gutter={[16, 32]}>
                                    <Col span={24}>
                                        <Form.Item
                                            label={t("ScenarioMitigation.lbClassification")}
                                        >
                                            <span>{data.classification?.name}</span>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </div>
                        </div>


                        {sections.current.map((item, index) => (
                            <div id={`section${item.id}`} key={index} className='form-section'>
                                <p className='section-title'>
                                    {item.title}
                                </p>
                                <item.sectionComponent
                                    data={data}
                                    gogoAppraisalTool={gotoAppraisalTool} />
                            </div>
                        ))}

                    </Form>
                </Col>
                <Col xs={0} sm={5}>
                    {UTIL.CheckShowReviewByState(data.submit_state) &&
                        <AppReview newReview={newReview} comment={comment} data={reviewData} showInputComment={ROLE_GUARD.isApprover(data.loss_scenario?.approval_name)} />}
                </Col>
            </Row>

            {data.submit_state === ReportStatesEnum.Submitted && ROLE_GUARD.isApprover(data.loss_scenario?.approval_name) && isApprovalWFL && <FooterAction onApprove={approve} onRefer={refer} />}
        </div>

    )
}

export default ScenarioMitigationView;
